/*
 * benvel-cookie-consent — Habeas Data cookie consent drawer web component.
 *
 * Story 8.1 — Ley 1581/2012 + Decreto 1377/2013 + Circular Externa 002/2015
 * SIC compliance (Colombian data protection law).
 *
 * Responsibilities:
 *  - Reads/writes the `benvel_consent` first-party cookie + localStorage mirror.
 *  - Calls Shopify.customerPrivacy.setTrackingConsent() to sync Shopify's
 *    native consent state with Benvel's drawer state.
 *  - Exposes `window.benvel.consent` adapter for Phase B analytics loaders.
 *  - Dispatches `benvel:cookie-consent-changed` on `document` on every state
 *    change and once on page-load hydration (decision: 'cached') when a
 *    consent cookie is already set.
 *  - Keyboard-accessible: native <dialog> focus trap + Esc handling.
 *  - PII-free: cookie/localStorage payload contains ONLY {v, ts, categories, decision}.
 *
 * Architecture anchors: architecture.md:613, 957, 1022, 1173, 1212, 1217, 1227, 1237.
 * Events registry: shopify-theme/docs/events.md (benvel:cookie-consent-changed).
 * Lock scope: asset:benvel-cookie-consent.js
 */

import { Component } from "@theme/component";

// ──────────────────────────────────────────────────────────────────────────────
// Constants
// ──────────────────────────────────────────────────────────────────────────────

const COOKIE_NAME = "benvel_consent";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 12 months in seconds
const SCHEMA_VERSION = 1;
const EVENT_NAME = "benvel:cookie-consent-changed";

// ──────────────────────────────────────────────────────────────────────────────
// Default consent state (AC2: default = reject all non-essential)
// ──────────────────────────────────────────────────────────────────────────────

const DEFAULT_STATE = Object.freeze({
  v: SCHEMA_VERSION,
  ts: null,
  categories: Object.freeze({
    essential: true,
    functional: false,
    analytics: false,
    marketing: false,
  }),
  decision: "pending",
});

// ──────────────────────────────────────────────────────────────────────────────
// Cookie helpers — no PII, no server-side reads
// ──────────────────────────────────────────────────────────────────────────────

function readConsentCookie() {
  const match = document.cookie
    .split(";")
    .map((c) => c.trim())
    .find((c) => c.startsWith(COOKIE_NAME + "="));
  if (!match) return null;
  try {
    return JSON.parse(decodeURIComponent(match.slice(COOKIE_NAME.length + 1)));
  } catch {
    return null;
  }
}

function writeConsentCookie(state) {
  const value = encodeURIComponent(JSON.stringify(state));
  document.cookie = [
    `${COOKIE_NAME}=${value}`,
    `Max-Age=${COOKIE_MAX_AGE}`,
    "Path=/",
    "SameSite=Lax",
    "Secure",
  ].join("; ");
}

function readConsentStorage() {
  try {
    const raw = localStorage.getItem(COOKIE_NAME);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeConsentStorage(state) {
  try {
    localStorage.setItem(COOKIE_NAME, JSON.stringify(state));
  } catch {
    // localStorage may be unavailable (private mode, security policy).
    // Graceful degradation: cookie-only persistence still works.
  }
}

function isStateValid(state) {
  if (!state || typeof state !== "object") return false;
  if (state.v !== SCHEMA_VERSION) return false;
  if (!state.ts || !state.categories || !state.decision) return false;
  if (state.decision === "pending") return false;
  // Check 12-month retention window.
  const ts = new Date(state.ts).getTime();
  if (isNaN(ts)) return false;
  const ageMs = Date.now() - ts;
  if (ageMs > COOKIE_MAX_AGE * 1000) return false;
  return true;
}

function buildState(categories, decision) {
  return {
    v: SCHEMA_VERSION,
    ts: new Date().toISOString(),
    categories: {
      essential: true,
      functional: Boolean(categories.functional),
      analytics: Boolean(categories.analytics),
      marketing: Boolean(categories.marketing),
    },
    decision,
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Shopify Customer Privacy API wrapper (AC4)
// ──────────────────────────────────────────────────────────────────────────────

function syncShopifyConsent(categories, callback) {
  if (typeof window.Shopify?.customerPrivacy?.setTrackingConsent !== "function") {
    console.warn(
      "[benvel-cookie-consent] Shopify.customerPrivacy not available. " +
        "Drawer functional, but native Shopify Web Pixels unsynced. " +
        "Enable Customer Privacy in Shopify Admin → Settings → Customer Privacy."
    );
    if (callback) callback();
    return;
  }
  window.Shopify.customerPrivacy.setTrackingConsent(
    {
      analytics: Boolean(categories.analytics),
      marketing: Boolean(categories.marketing),
      preferences: Boolean(categories.functional),
      sale_of_data: false, // CO Habeas Data: never opt-in to data sale
    },
    callback || (() => {})
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Event dispatch (AC7)
// Dispatched on `document` (canonical event bus per architecture.md:1227).
// ──────────────────────────────────────────────────────────────────────────────

function dispatchConsentEvent(state) {
  // benvel:cookie-consent-changed — registered in shopify-theme/docs/events.md
  document.dispatchEvent(
    new CustomEvent(EVENT_NAME, {
      bubbles: false,
      cancelable: false,
      detail: {
        accepted: state.categories.analytics || state.categories.marketing,
        categories: Object.keys(state.categories).filter(
          (k) => state.categories[k]
        ),
        decision: state.decision,
        timestamp: state.ts,
      },
    })
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// window.benvel.consent adapter — Phase B analytics loaders consume this (AC4)
// ──────────────────────────────────────────────────────────────────────────────

function initAdapter(stateRef) {
  window.benvel = window.benvel || {};
  window.benvel.consent = {
    DEFAULT_STATE,
    get state() {
      return stateRef.current;
    },
    has(category) {
      return Boolean(stateRef.current?.categories?.[category]);
    },
    onChange(callback) {
      const handler = (e) => callback(e.detail);
      document.addEventListener(EVENT_NAME, handler);
      return () => document.removeEventListener(EVENT_NAME, handler);
    },
    setTrackingConsent(partialCategories) {
      // Programmatic write: used by footer revoke link (future Story 8.6).
      const merged = {
        ...stateRef.current.categories,
        ...partialCategories,
        essential: true, // essential always on
      };
      const decision = "custom";
      const newState = buildState(merged, decision);
      stateRef.current = newState;
      writeConsentCookie(newState);
      writeConsentStorage(newState);
      syncShopifyConsent(merged, () => dispatchConsentEvent(newState));
    },
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Web Component
// ──────────────────────────────────────────────────────────────────────────────

class BenvelCookieConsent extends Component {
  // Mutable reference object so the adapter closure always reads current state.
  #stateRef = { current: { ...DEFAULT_STATE } };
  #customizePanel = null;
  #personalizeBtn = null;

  connectedCallback() {
    super.connectedCallback();

    // Initialise adapter immediately so Phase B loaders can subscribe even
    // before the cookie is resolved.
    initAdapter(this.#stateRef);

    // Try to resolve consent from cookie, falling back to localStorage.
    let resolved = readConsentCookie();
    if (!isStateValid(resolved)) {
      resolved = readConsentStorage();
    }

    if (isStateValid(resolved)) {
      // Consent already given — mirror cookie from localStorage if needed,
      // update state, hide drawer, fire cached replay event (AC7.e).
      this.#stateRef.current = resolved;
      writeConsentCookie(resolved); // re-stamp to refresh Max-Age
      writeConsentStorage(resolved);
      this.hidden = true;
      const replayState = { ...resolved, decision: "cached" };
      dispatchConsentEvent(replayState);
      return;
    }

    // No valid consent yet — show drawer.
    this.#stateRef.current = { ...DEFAULT_STATE };
    this.#bindHandlers();
    // Trigger slide-up entrance after paint.
    requestAnimationFrame(() => {
      this.removeAttribute("hidden");
      this.dataset.state = "visible";
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  #bindHandlers() {
    // Main drawer buttons
    const acceptBtn = this.querySelector('[data-consent-action="accept"]');
    const rejectBtn = this.querySelector('[data-consent-action="reject"]');
    const personalizeBtn = this.querySelector(
      '[data-consent-action="customize"]'
    );
    this.#personalizeBtn = personalizeBtn;

    if (acceptBtn) {
      acceptBtn.addEventListener("click", this.#onAccept);
    }
    if (rejectBtn) {
      rejectBtn.addEventListener("click", this.#onReject);
    }
    if (personalizeBtn) {
      personalizeBtn.addEventListener("click", this.#onCustomize);
    }

    // Customize panel
    const panel = this.querySelector(
      "dialog.benvel-cookie-consent__customize-panel"
    );
    this.#customizePanel = panel;
    if (panel) {
      const saveBtn = panel.querySelector('[data-consent-action="save"]');
      const closeBtn = panel.querySelector('[data-consent-action="close"]');
      if (saveBtn) saveBtn.addEventListener("click", this.#onSavePreferences);
      if (closeBtn)
        closeBtn.addEventListener("click", this.#onCloseCustomizePanel);
      // Esc on the panel dialog is handled natively by the browser for native
      // <dialog> — we override to return focus to Personalizar (AC6.c).
      panel.addEventListener("cancel", this.#onPanelCancel);
    }

    // Esc on main drawer does NOT dismiss (AC6.d).
    this.addEventListener("keydown", this.#onDrawerKeydown);
  }

  // ── Action handlers ──────────────────────────────────────────────────────

  #onAccept = () => {
    const state = buildState(
      { essential: true, functional: true, analytics: true, marketing: true },
      "accept"
    );
    this.#persistAndClose(state);
  };

  #onReject = () => {
    const state = buildState(
      { essential: true, functional: false, analytics: false, marketing: false },
      "reject"
    );
    this.#persistAndClose(state);
  };

  #onCustomize = () => {
    if (!this.#customizePanel) return;
    // Reset panel checkboxes to current state (default = all non-essential off).
    const categories = this.#stateRef.current.categories;
    const panel = this.#customizePanel;
    for (const checkbox of panel.querySelectorAll(
      'input[type="checkbox"]:not(:disabled)'
    )) {
      const cat = checkbox.dataset.category;
      if (cat) {
        checkbox.checked = Boolean(categories[cat]);
      }
    }
    panel.showModal();
  };

  #onSavePreferences = () => {
    if (!this.#customizePanel) return;
    const categories = { essential: true };
    for (const checkbox of this.#customizePanel.querySelectorAll(
      'input[type="checkbox"]:not(:disabled)'
    )) {
      const cat = checkbox.dataset.category;
      if (cat) {
        categories[cat] = checkbox.checked;
      }
    }
    // Determine decision type.
    const allOn =
      categories.functional && categories.analytics && categories.marketing;
    const allOff =
      !categories.functional && !categories.analytics && !categories.marketing;
    const decision = allOn ? "accept" : allOff ? "reject" : "custom";
    const state = buildState(categories, decision);

    this.#customizePanel.close();
    if (this.#personalizeBtn) this.#personalizeBtn.focus();
    this.#persistAndClose(state);
  };

  #onCloseCustomizePanel = () => {
    if (!this.#customizePanel) return;
    this.#customizePanel.close();
    if (this.#personalizeBtn) this.#personalizeBtn.focus();
  };

  // Native <dialog> fires "cancel" on Esc — prevent default close (which would
  // call dialog.close()) and instead close + return focus (AC6.c).
  #onPanelCancel = (event) => {
    event.preventDefault();
    if (this.#customizePanel) this.#customizePanel.close();
    if (this.#personalizeBtn) this.#personalizeBtn.focus();
  };

  // Esc on the main drawer does NOT dismiss (AC6.d — drawer needs explicit action).
  #onDrawerKeydown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
    }
  };

  // ── Persist + close ──────────────────────────────────────────────────────

  #persistAndClose(state) {
    this.#stateRef.current = state;
    writeConsentCookie(state);
    writeConsentStorage(state);
    syncShopifyConsent(state.categories, () => {
      dispatchConsentEvent(state);
    });
    this.dataset.state = "hidden";
    // Brief transition then hide from DOM (so it doesn't consume tab order).
    const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ? 0
      : 150;
    window.setTimeout(() => {
      this.hidden = true;
    }, delay);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// Registration
// ──────────────────────────────────────────────────────────────────────────────

if (!customElements.get("benvel-cookie-consent")) {
  customElements.define("benvel-cookie-consent", BenvelCookieConsent);
}
